import type { Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import {
  array,
  looseObject,
  nullable,
  nullish,
  number,
  object,
  partialCheck,
  pipe,
  record,
  safeParse,
  string,
  unknown,
} from "valibot";
import { dataName, errorTranslation, integerValue, label, requireText } from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import { EXISTS_CONDITION_KEYS, MAX_CHARACTERS_XL, MAX_CHARACTERS_XS, MAX_NUMBER_SM } from "../../constants";
import { useFormServices } from "../../services/formServices";
import SubformInput from "../SubformInput";
import { NumberInput } from "../../components/inputs/NumberInput";
import { isNil } from "lodash-es";
import { subformReference } from "../../utils/validation/idReferences";
import MixedInput from "../../components/inputs/MixedInput";
import { createMemo, For } from "solid-js";
import FormServicesFactory from "../../services/FormServicesFactory";
import { toFieldValueOptions } from "../../utils/selectOptionsUtil";
import { SUMMARIZABLE_FIELDS } from "../../utils/fieldUtil";
import {
  parseStringTemplate,
  parseStringTemplateForField,
  renderStringTemplate,
  stringTemplateToString,
} from "../../utils/stringTemplateUtil";
import { stringTemplate } from "../../utils/validation/stringTemplate";
import { SUMMARIZABLE_VARIABLES, toVariableValueOption } from "../../variables/variableUtil";
import { isFilledInFilter } from "../../pages/customer/form/filter";
import DetailModal from "../../components/modal/DetailModal";
import SimpleTable from "../../components/table/SimpleTable";
import { renderFieldValue } from "../fieldValueRenderer";
import ButtonLink from "../../components/link/ButtonLink";
import { hasData } from "../fields";
import { useModalManager } from "../../components/modal/ModalManager";
import { FieldButtonPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import GlobalErrorInlineMessage from "../GlobalErrorInlineMessage";
import { keyFromSelector } from "i18next";

const DtoSchema = object({
  label_text: string(),
  data_name: string(),
  target_form_id: string(),
  form: nullish(looseObject({})),
  add_button_text: string(),
  itemHtml: string(),
  min_items: nullable(number()),
  max_items: nullable(number()),
});

const DomainSchema = pipe(
  object({
    label_text: label(),
    data_name: dataName(),
    target_form_id: subformReference(),
    add_button_text: requireText(MAX_CHARACTERS_XS),
    itemHtml: stringTemplate(SUMMARIZABLE_FIELDS, SUMMARIZABLE_VARIABLES, [], MAX_CHARACTERS_XL),
    min_items: nullable(integerValue(0, MAX_NUMBER_SM)),
    max_items: nullable(integerValue(1, MAX_NUMBER_SM)),
  }),
  partialCheck(
    [["min_items"], ["max_items"]],
    ({ min_items, max_items }) => (max_items ?? MAX_NUMBER_SM) >= (min_items ?? 0),
    errorTranslation(keyFromSelector(($) => $.VALIDATION_NUMERIC_MAX_GREATER_EQUALS_MIN)),
  ),
);

const ValueSchema = array(record(string(), unknown()));

export type SubformFieldProperties = InferOutput<typeof DomainSchema>;
export type SubformFieldDtoType = InferOutput<typeof DtoSchema>;
type ValueType = InferOutput<typeof ValueSchema>;

const SubformField: Field<SubformFieldProperties, SubformFieldDtoType, ValueType> = {
  type: "subform",
  category: "subform",
  widget: "com.moreapps:detail:1",
  schema: DomainSchema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => {
    const { t } = useTranslation();
    return {
      target_form_id: undefined,
      add_button_text: t(($) => $.FIELD_PROPERTY_SUBFORM_ADD_BUTTON_TEXT_DEFAULT_VALUE),
      itemHtml: parseStringTemplate(
        t(($) => $.FIELD_PROPERTY_SUBFORM_SUMMARY_IN_APP_DEFAULT_VALUE),
        [],
        [],
      ),
      min_items: null,
      max_items: null,
    };
  },
  ruleBehaviour: {
    conditions: { keys: EXISTS_CONDITION_KEYS, variant: "text" },
    actions: { variant: "none" },
  },
  filter: {
    type: "none",
    definition: [isFilledInFilter],
  },
  renderValue: (value, { fieldConfig, formVersion }) => {
    const subformFields = formVersion.fields.filter(({ scope }) => fieldConfig.properties.target_form_id.equals(scope));

    return (
      <ol>
        <For each={value}>
          {(item, idx) => {
            const { open } = useModalManager();
            const summary = renderStringTemplate(fieldConfig.properties.itemHtml, subformFields, item);

            return (
              <li>
                <ButtonLink
                  label={`${idx() + 1}. ${summary}`}
                  onClick={() =>
                    open({
                      content: () => (
                        <DetailModal
                          title={fieldConfig.properties.label_text}
                          iconTile={{ icon: SubformField.icon, category: SubformField.category }}
                          content={() => {
                            const data = subformFields.filter(hasData).map((subFieldConfig) => ({
                              label: subFieldConfig.properties.label_text,
                              value: renderFieldValue(
                                item[subFieldConfig.properties.data_name],
                                subFieldConfig,
                                formVersion,
                              ),
                            }));

                            return <SimpleTable data={data} />;
                          }}
                        />
                      ),
                    })
                  }
                />
              </li>
            );
          }}
        </For>
      </ol>
    );
  },
  icon: "documentDuplicate",
  titleKey: keyFromSelector(($) => $.SUBFORM_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.SUBFORM_FIELD_DESCRIPTION),
  propertyKeys: {
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    target_form_id: keyFromSelector(($) => $.FIELD_PROPERTY_SUBFORM_TARGET_FORM_ID),
    itemHtml: keyFromSelector(($) => $.FIELD_PROPERTY_SUBFORM_SUMMARY_IN_APP),
    min_items: keyFromSelector(($) => $.FIELD_PROPERTY_SUBFORM_MIN_ITEMS),
    max_items: keyFromSelector(($) => $.FIELD_PROPERTY_SUBFORM_MAX_ITEMS),
    add_button_text: keyFromSelector(($) => $.FIELD_PROPERTY_SUBFORM_ADD_BUTTON_TEXT),
  },
  toDto: (domain, fields, variables) => {
    const { output } = safeParse(DtoSchema, {
      ...domain,
      itemHtml: stringTemplateToString(domain.itemHtml, fields, variables),
    });
    return output as SubformFieldDtoType;
  },
  fromDto: (dto) => {
    const { output } = safeParse(DomainSchema, dto);

    // Include form for mapping to linked subform
    return { ...(output as SubformFieldProperties), form: dto.form };
  },
  postProcessFromDto: (domain, fields, variables) => ({
    ...domain,
    itemHtml: parseStringTemplateForField(
      domain.itemHtml,
      isNil(domain.target_form_id) ? [] : fields.filter((field) => domain.target_form_id.equals(field.scope)),
      variables,
    ),
  }),
  PreviewContent: (props) => (
    <PreviewContentLabel
      uid={props.uid}
      label={props.properties.label_text}
      required={!isNil(props.properties.min_items) && props.properties.min_items > 0}
    >
      <FieldButtonPreview icon="plus" text={props.properties.add_button_text} />
    </PreviewContentLabel>
  ),
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService, variableService } = useFormServices();

    const subformFields = createMemo(() =>
      isNil(props.data.properties.target_form_id)
        ? []
        : FormServicesFactory.forSubform(props.data.properties.target_form_id).fieldService.getFieldsByTypes(
            SUMMARIZABLE_FIELDS,
          ),
    );
    const subformFieldsOptions = createMemo(() => ({
      Fields: subformFields().flatMap((field) => toFieldValueOptions(field)),
      Variables: [{ Custom: variableService.getVariablesByTypes(SUMMARIZABLE_VARIABLES).map(toVariableValueOption) }],
    }));

    const globalErrors = createMemo(() => props.getGlobalErrors());

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => fieldService.setLabelProperty(props.uid, e.target.value)}
            errorMessage={props.getError("label_text")}
            required
          />
          <TextInput
            label={t(props.propertyKeys.add_button_text)}
            value={props.data.properties.add_button_text}
            onBlur={(e) => props.setValue("add_button_text", e.target.value)}
            errorMessage={props.getError("add_button_text")}
            required
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_SOURCE)}>
          <SubformInput
            label={t(($) => $.FIELD_PROPERTY_SUBFORM_TARGET_FORM_ID)}
            value={!isNil(props.data.properties.target_form_id) ? props.data.properties.target_form_id : null}
            onChange={(subformId) => props.setValue("target_form_id", subformId)}
            errorMessage={props.getError("target_form_id")}
            required
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_VALIDATION)}>
          <NumberInput
            label={t(props.propertyKeys.min_items)}
            value={props.data.properties.min_items}
            minValue={0}
            maxValue={MAX_NUMBER_SM}
            onBlur={(value) => props.setValue("min_items", value)}
            errorMessage={props.getError("min_items")}
          />
          <NumberInput
            label={t(props.propertyKeys.max_items)}
            value={props.data.properties.max_items}
            minValue={1}
            maxValue={MAX_NUMBER_SM}
            onBlur={(value) => props.setValue("max_items", value)}
            errorMessage={props.getError("max_items")}
          />
          <GlobalErrorInlineMessage errors={globalErrors()} />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <MixedInput
            label={t(props.propertyKeys.itemHtml)}
            value={props.data.properties.itemHtml}
            parse={(value) =>
              parseStringTemplate(value, subformFields(), variableService.getVariablesByTypes(SUMMARIZABLE_VARIABLES))
            }
            onChange={(value) => props.setValue("itemHtml", value)}
            errorMessage={props.getError("itemHtml")}
            required
            info={{
              title: t(props.propertyKeys.itemHtml),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.itemHtml }),
              description: t(($) => $.FIELD_PROPERTY_SUBFORM_SUMMARY_IN_APP_DESCRIPTION),
            }}
            options={subformFieldsOptions()}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(SubformField);
